import { motion } from 'motion/react';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'María G.',
    role: 'Estudiante de Preparatoria',
    country: 'Mexico',
    flag: '🇲🇽',
    avatar: 'M',
    color: 'from-pink-500 to-rose-500',
    text: 'Frappi me salvó el semestre. Antes olvidaba tareas todo el tiempo, ahora tengo todo organizado y mis calificaciones subieron un montón.',
    rating: 5,
  },
  {
    name: 'Carlos R.',
    role: 'Estudiante Universitario',
    country: 'Colombia',
    flag: '🇨🇴',
    avatar: 'C',
    color: 'from-blue-500 to-cyan-500',
    text: 'La función de escanear tareas con la cámara es increíble. Le tomo foto al pizarrón y listo, todo queda registrado automáticamente.',
    rating: 5,
  },
  {
    name: 'Ana L.',
    role: 'Mamá de 2 estudiantes',
    country: 'Argentina',
    flag: '🇦🇷',
    avatar: 'A',
    color: 'from-violet-500 to-purple-500',
    text: 'Por fin puedo ver las tareas de mis dos hijos en un solo lugar. Las alertas me avisan cuando hay algo urgente. ¡Súper recomendada!',
    rating: 5,
  },
  {
    name: 'Diego M.',
    role: 'Estudiante de Secundaria',
    country: 'Peru',
    flag: '🇵🇪',
    avatar: 'D',
    color: 'from-amber-500 to-orange-500',
    text: 'Los planes de estudio con IA son geniales. Me dice exactamente qué estudiar cada día y cuánto tiempo dedicarle a cada materia.',
    rating: 5,
  },
  {
    name: 'Valentina S.',
    role: 'Estudiante de Preparatoria',
    country: 'Chile',
    flag: '🇨🇱',
    avatar: 'V',
    color: 'from-green-500 to-emerald-500',
    text: 'Me encanta el sistema de rachas y logros. Me motiva a completar mis tareas todos los días. Ya llevo 45 días seguidos.',
    rating: 5,
  },
  {
    name: 'Roberto P.',
    role: 'Papá de estudiante',
    country: 'Mexico',
    flag: '🇲🇽',
    avatar: 'R',
    color: 'from-[#a67eec] to-[#8b6de0]',
    text: 'El precio es muy accesible comparado con otras apps. Y lo mejor es que la IA viene incluida, no hay cargos extras escondidos.',
    rating: 5,
  },
  {
    name: 'Luciana F.',
    role: 'Estudiante Universitaria',
    country: 'Brasil',
    flag: '🇧🇷',
    avatar: 'L',
    color: 'from-teal-500 to-cyan-500',
    text: 'Uso Frappi todos los días. El calendario me muestra todo lo que tengo pendiente y la IA me ayuda cuando no entiendo algo.',
    rating: 5,
  },
  {
    name: 'Santiago A.',
    role: 'Estudiante de Secundaria',
    country: 'Colombia',
    flag: '🇨🇴',
    avatar: 'S',
    color: 'from-rose-500 to-pink-500',
    text: 'Antes usaba Notion pero era muy complicado. Frappi es mucho más fácil y está diseñada justo para lo que necesito como estudiante.',
    rating: 5,
  },
];

function TestimonialCard({ testimonial }: { testimonial: typeof testimonials[0] }) {
  return (
    <div className="w-[320px] md:w-[360px] shrink-0 mx-2">
      <div className="h-full p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-border/50 hover:border-primary/20 transition-all duration-300 hover:shadow-lg group">
        {/* Quote icon */}
        <Quote className="w-8 h-8 text-primary/15 mb-3 group-hover:text-primary/25 transition-colors" />

        {/* Stars */}
        <div className="flex gap-0.5 mb-3">
          {Array.from({ length: testimonial.rating }).map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
          ))}
        </div>

        {/* Text */}
        <p className="text-foreground/80 text-sm leading-relaxed mb-5">
          "{testimonial.text}"
        </p>

        {/* Author */}
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${testimonial.color} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
            {testimonial.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              {testimonial.name}
              <span className="text-base">{testimonial.flag}</span>
            </p>
            <p className="text-xs text-muted-foreground">{testimonial.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Testimonials() {
  const firstRow = testimonials.slice(0, 4);
  const secondRow = testimonials.slice(4);

  return (
    <section className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/20 via-background to-muted/30" />

      <div className="relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-16 px-4"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary/20 text-primary text-sm font-semibold mb-6 shadow-sm"
          >
            <Star size={14} className="fill-amber-400 text-amber-400" />
            Testimonios
          </motion.span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 tracking-tight">
            Lo que dicen{' '}
            <span className="bg-gradient-to-r from-[#a67eec] via-[#5bb4f2] to-[#5ddba3] bg-clip-text text-transparent">
              nuestros usuarios
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Miles de estudiantes y familias en LATAM ya confían en Frappi
          </p>
        </motion.div>

        {/* Marquee Row 1 */}
        <div className="marquee-container mb-4">
          <div className="flex animate-marquee">
            {[...firstRow, ...firstRow].map((testimonial, i) => (
              <TestimonialCard key={`r1-${i}`} testimonial={testimonial} />
            ))}
          </div>
        </div>

        {/* Marquee Row 2 - Reverse */}
        <div className="marquee-container">
          <div className="flex animate-marquee-reverse">
            {[...secondRow, ...secondRow].map((testimonial, i) => (
              <TestimonialCard key={`r2-${i}`} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
